import type { Language } from '@/i18n/translations';

export type PageKey = 'index' | 'admin' | 'deploy' | 'global';

export type SectionKey =
  | 'welcome'
  | 'canvas-basics'
  | 'blocks'
  | 'connectors'
  | 'groups-layers'
  | 'multi-sheet'
  | 'layer-nav'
  | 'save-load'
  | 'shortcuts'
  | 'advanced'
  | 'generic';

export type PlacementSide = 'auto' | 'top' | 'bottom' | 'left' | 'right';
export type PlanGate = 'free' | 'advanced' | 'pro' | 'max' | 'enterprise';

export type ExperienceLevel = 'beginner' | 'experienced' | 'power-user';
export type UserRole = 'designer' | 'developer' | 'business' | 'other';

// 7 use-cases that mirror the 7 WorkflowWizardDialog templates.
export type UseCase =
  | 'it-infrastructure'   // it-infra
  | 'business-process'    // business
  | 'organization-chart'  // org
  | 'decision-tree'       // decision
  | 'mind-map'            // mindmap
  | 'project-planning'    // project
  | 'sandbox';            // advanced (blank canvas, skip)

export type Interest = 'ai-assistant' | '3d-view' | 'multi-sheet' | 'export-share' | 'shortcuts' | 'process-motor' | 'enterprise';

export interface UserPreferences {
  experience: ExperienceLevel;
  role: UserRole;
  useCase: UseCase;
  interests: Interest[];
}

/**
 * Advance condition for a step. `next` (default) means the Next button. Any
 * other variant hides Next and waits for the matching interaction to fire
 * `dispatchOnboardingEvent`. Skip and Esc still work as escape hatches.
 */
export type AdvanceOn =
  | { kind: 'next' }
  | { kind: 'click'; target?: string }
  | { kind: 'dblclick'; target?: string }
  | { kind: 'contextmenu'; target?: string }
  | { kind: 'drop'; target?: string }
  | { kind: 'move'; target?: string }
  | { kind: 'connectorCreated' }
  | { kind: 'layerEntered' }
  | { kind: 'groupCreated' }
  /**
   * Advances when a context-menu item is selected from a custom right-click
   * menu (BlockContextMenu/LayerContextMenu/GroupContextMenu). If `action`
   * is provided, only that specific action advances; otherwise any menu
   * click advances. The action string matches the menu item's data-guide
   * (e.g. "ctx-group", "ctx-rename").
   */
  | { kind: 'menuItemSelected'; action?: string }
  | { kind: 'event'; name: string };

/**
 * Animation path for multi-target demos (e.g. connector-draw). The spotlight
 * smoothly tweens between each target selector in sequence and loops until the
 * step advances. Selectors are plain CSS (queried with querySelector).
 */
export interface StepAnim {
  path: string[];
  durationMs?: number;
  loop?: boolean;
}

export interface GuideStep {
  id: string;
  page: PageKey;
  section: SectionKey;
  target: string;
  /**
   * Fallback targets tried in order when `target` can't be resolved (e.g. the
   * canvas has only layers and no regular blocks). The first one found wins.
   */
  fallbackTargets?: string[];
  mobileTarget?: string | null;
  title: Partial<Record<Language, string>> & { en: string };
  body: Partial<Record<Language, string>> & { en: string };
  placement?: PlacementSide;
  padding?: number;
  radius?: number;
  clickTarget?: boolean;
  centered?: boolean;
  /** Interaction required to advance. Defaults to `{ kind: 'next' }`. */
  advanceOn?: AdvanceOn;
  /** Optional animated multi-target demo overlaid on the static highlight. */
  anim?: StepAnim;
  /** Optional secondary highlights drawn at the same time as `target`. */
  highlights?: string[];
  /**
   * Extra CSS selector whose resolved element is added to the spotlight as a
   * union-rect alongside the primary target. Useful for highlighting a Radix
   * portal (e.g. an open context menu) that doesn't live in the main tree.
   * Resolved live so the rect follows opening/closing portals.
   */
  additionalHighlight?: string;
  /**
   * Convenience: automatically extend the spotlight to any currently-open
   * Radix context/dropdown menu while this step is active. Equivalent to
   * setting `additionalHighlight` to `[role="menu"][data-state="open"]`.
   */
  includeOpenMenus?: boolean;
  /**
   * Optional one-shot "Show me how" demo. When present, a button appears in
   * the tooltip that plays a visible mouse cursor through the given steps
   * (clicks, right-clicks, drags) to teach the gesture.
   */
  showMeHow?: {
    /**
     * Each step resolves a CSS selector (supports our `::eq(N)` suffix) and
     * optionally performs an action when the cursor arrives (`click`,
     * `rightclick`, `dblclick`, `press`, `release`, `hover`). A drag demo is
     * a `press` followed by subsequent selectors and ending in `release`.
     * `label` annotates the cursor during the hold, `hold` sets ms to pause.
     */
    steps: Array<{
      selector: string;
      action?: 'click' | 'rightclick' | 'dblclick' | 'press' | 'release' | 'hover';
      label?: string;
      hold?: number;
      /**
       * Also dispatch a real DOM event at this cursor position. Use ONLY
       * for "opener" gestures (e.g. right-click to open a context menu,
       * click to open a dropdown) so the NEXT demo step has a real target
       * to aim at. Don't set this on the final action — we don't want the
       * demo to complete the user's task for them.
       */
      trigger?: boolean;
    }>;
    durationMs?: number;
  };
  /**
   * Short "Waiting for you to …" hint shown in the tooltip when `advanceOn`
   * is an interaction. Defaults inferred from the AdvanceOn kind.
   */
  waitingHint?: Partial<Record<Language, string>> & { en: string };
  minPlan?: PlanGate;
  requiresAuth?: boolean;
  useCases?: UseCase[];
  roles?: UserRole[];
  experience?: ExperienceLevel[];
  interests?: Interest[];
  order?: number;
  region?: 'sidebar-left' | 'sidebar-right' | 'mobile-tools' | 'mobile-properties' | 'none';
}

export interface OnboardingProgress {
  preferences: UserPreferences | null;
  completed_sections: SectionKey[];
  current_section: SectionKey | null;
  current_step_index: number;
  dismissed_welcome: boolean;
  skipped: boolean;
  started_at: string;
  completed_at: string | null;
  updated_at: string;
}

export interface AllSectionItem {
  id: SectionKey;
  page: PageKey;
  label: Partial<Record<Language, string>> & { en: string };
  /** One-line description shown on the TourPicker card. */
  description?: Partial<Record<Language, string>> & { en: string };
  /** Estimated minutes — shown as "~Nm" on the picker. */
  estMinutes?: number;
  /** Whether to badge this tour as Recommended. */
  recommended?: boolean;
  minPlan?: PlanGate;
  interest?: Interest;
}

export interface OnboardingContextValue {
  lang: Language;
  isActive: boolean;
  introDialogOpen: boolean;
  activePage: PageKey | null;
  activeSection: SectionKey | null;
  currentStepIndex: number;
  steps: GuideStep[];
  progress: OnboardingProgress | null;
  shouldShowIntro: boolean;
  shouldShowGenericTour: boolean;
  pickerVisible: boolean;
  setPickerVisible: (v: boolean) => void;
  /**
   * True while the WorkflowWizardDialog ("What are you building?") is open.
   * The generic tour is suppressed while this is true so the two don't stack
   * on top of each other on a user's first visit.
   */
  wizardOpen: boolean;
  setWizardOpen: (v: boolean) => void;
  /**
   * Generalised modal-suppress counter. Any dialog that wants to pause
   * the tour calls `pushOpenModal()` on open and `popOpenModal()` on
   * close. Tour auto-start is gated on `openModalCount === 0` so
   * stacked modals don't clobber each other.
   */
  openModalCount: number;
  pushOpenModal: () => void;
  popOpenModal: () => void;
  openIntroDialog: () => void;
  closeIntroDialog: () => void;
  /** Used by the (removed) custom intro dialog; kept for back-compat. */
  completeIntro: (preferences: UserPreferences) => void;
  /** Driven by WorkflowWizardDialog.onComplete — maps templateId to a UseCase. */
  completeIntroFromTemplate: (templateId: string) => void;
  skipOnboarding: () => void;
  startTour: (section: SectionKey, opts?: { fromStep?: number }) => void;
  endTour: () => void;
  next: () => void;
  prev: () => void;
  skipStep: () => void;
  goTo: (i: number) => void;
  markSectionCompleted: (s: SectionKey) => void;
  updatePreferences: (prefs: Partial<UserPreferences>) => void;
  resetAll: () => void;
  allSections: AllSectionItem[];
}

export const EMPTY_PROGRESS: OnboardingProgress = {
  preferences: null,
  completed_sections: [],
  current_section: null,
  current_step_index: 0,
  dismissed_welcome: false,
  skipped: false,
  started_at: '',
  completed_at: null,
  updated_at: '',
};
